import {
  CardWithBadge,
  Chip,
  ChipColor,
  Collapse,
  CollapseCompact,
  FileInputButton,
  FilterSearch,
  UIInputSelect,
  InfoCard,
  InputSearch,
  SkeletonContent,
  formatDateHour,
  Button,
  Card,
} from "@endya-conformite/ui-kit";
import Download from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import AddIcon from "@endya-conformite/ui-kit/src/assets/icons/plus-circle.svg?react";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import PreviewIcon from "@endya-conformite/ui-kit/src/assets/icons/preview.svg?react";
import { DocumentsAssociesProps } from "@src/components/sidebar/sidebar.types";
import { useCQAxeWithFilesPaginatedQuery } from "@src/data/controle-quinquennal/hook/useCQAxeWithFilesQuery";
import { useEntreprise } from "@src/data/entreprise/hook/useEntreprise";
import { controleQuinquennalKeys } from "@src/data/controle-quinquennal/hook/controle-quinquennal.keys";
import { useAddFileMutation } from "@src/data/files/hook/useAddFileMutation";
import { useTaxonomieQuery } from "@src/data/taxonomie/hook/useTaxonomieQuery";
import { useDownloadFileMutation } from "@src/data/files/hook/useDownloadFileMutation";
import { useViewFileMutation } from "@src/data/files/hook/useViewFileMutation";
import { useZipExport } from "@src/data/files/hook/useZipFilesMutation";
import { useZipMetadataQuery } from "@src/data/files/hook/useZipMetadataQuery";
import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  AxeFileUploader,
  buildAxeFileGroups,
} from "./documentsAssocies.groups";
import styles from "./SidebarDroite.module.scss";

const FILES_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 500;

const UPLOADER_BADGE: Record<
  AxeFileUploader,
  { label: string; color: ChipColor }
> = {
  ADHERENT: { label: "Adhérent", color: ChipColor.Gray },
  VERIFICATEUR: { label: "Vérificateur", color: ChipColor.Green },
};

export function DocumentsAssocies({
  idCq,
  idAxe,
  idEntreprise,
  millesime,
  isAssujetti,
  isReadOnly,
}: DocumentsAssociesProps) {
  const { data: entreprise, isLoading: entrepriseIsLoading } = useEntreprise(
    millesime,
    idEntreprise
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typePieceDeclare, setTypePieceDeclare] = useState<string>("");
  const [viewingFileId, setViewingFileId] = useState<string | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null
  );
  const [groupOpenOverrides, setGroupOpenOverrides] = useState<
    Record<string, boolean>
  >({});
  const toggleGroup = (groupKey: string, isCurrentlyOpen: boolean) =>
    setGroupOpenOverrides((prev) => ({
      ...prev,
      [groupKey]: !isCurrentlyOpen,
    }));

  const { data: filesData, isLoading: axeIsLoading } =
    useCQAxeWithFilesPaginatedQuery(idCq, idAxe, debouncedSearch, FILES_LIMIT);

  const { data: totalResponse } = useCQAxeWithFilesPaginatedQuery(
    idCq,
    idAxe,
    "",
    FILES_LIMIT
  );

  const { mutateAsync: addFileAsync } = useAddFileMutation();
  const { data: taxonomie } = useTaxonomieQuery();
  const [uploadingCount, setUploadingCount] = useState(0);
  const isAddingFile = uploadingCount > 0;
  const { mutate: viewFile } = useViewFileMutation();
  const { mutate: downloadFile } = useDownloadFileMutation();
  const queryClient = useQueryClient();
  const { data: zipMetadata } = useZipMetadataQuery(idCq, idAxe, {
    enabled: isOpen,
  });
  const { triggerExport, isExporting: isZippingFiles } = useZipExport({
    controleQuinquennalId: idCq,
    axeId: idAxe,
    onExported: () => {
      if (!idCq || !idAxe) return;
      queryClient
        .invalidateQueries({
          queryKey: controleQuinquennalKeys.zipMetadata(idCq, idAxe),
        })
        .catch(() => {});
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleViewFile = (
    fileId: string,
    pathname: string,
    filename: string,
    siren: string,
    year: number
  ) => {
    setViewingFileId(fileId);
    viewFile(
      { siren, millesime: year, pathname, filename },
      { onSettled: () => setViewingFileId(null) }
    );
  };

  const handleDownloadFile = (
    fileId: string,
    pathname: string,
    filename: string,
    siren: string,
    year: number
  ) => {
    setDownloadingFileId(fileId);
    downloadFile(
      { siren, millesime: year, pathname, filename },
      { onSettled: () => setDownloadingFileId(null) }
    );
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const uploadFiles = useCallback(
    (files: File[]) => {
      if (!idCq || !idAxe) return;
      files.forEach((file) => {
        setUploadingCount((prev) => prev + 1);
        addFileAsync({
          controleQuinquennalId: idCq,
          axeId: idAxe,
          file,
          typePieceDeclare: typePieceDeclare || undefined,
        })
          .catch(() => {})
          .finally(() => setUploadingCount((prev) => prev - 1));
      });
      // Un type vaut pour le dépôt qu'on vient de faire, pas pour le suivant :
      // sans remise à zéro, la pièce d'après hériterait silencieusement du
      // type de la précédente.
      setTypePieceDeclare("");
    },
    [idCq, idAxe, addFileAsync, typePieceDeclare]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      uploadFiles(Array.from(e.dataTransfer.files));
    },
    [uploadFiles]
  );

  const handleAddFile = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    uploadFiles(Array.from(event.target.files));
  };

  if (!filesData || entrepriseIsLoading || !entreprise)
    return (
      <Collapse
        isOpen={isOpen}
        title="Pièces justificatives"
        onToggle={() => setIsOpen((prevState) => !prevState)}
        hasToggleText={false}
      >
        <div className={classNames(styles.filesContainer, styles.scroll)}>
          <SkeletonContent />
        </div>
      </Collapse>
    );

  const axeWithPaginatedFiles = filesData.axe;
  const optionsTypePiece = (
    taxonomie?.typesPieceParAxeType[axeWithPaginatedFiles.type] ?? []
  ).flatMap((slug) => {
    const type = taxonomie?.typesPiece.find((t) => t.slug === slug);
    return type ? [{ label: type.label, value: type.slug }] : [];
  });
  const totalFiles = filesData.total;
  const totalInitial = totalResponse?.total ?? totalFiles;
  const displayedFiles = axeWithPaginatedFiles.controleQuinquennalAxeFiles;
  const isBusy =
    viewingFileId !== null || downloadingFileId !== null || isZippingFiles;

  const fileGroups = buildAxeFileGroups(displayedFiles);

  const renderFileCard = (file: (typeof displayedFiles)[number]) => {
    const isThisViewing = viewingFileId === file.idFile;
    const isThisDownloading = downloadingFileId === file.idFile;
    const badge = file.uploadBy ? UPLOADER_BADGE[file.uploadBy] : null;
    return (
      <Card className={styles.card} key={file.idFile}>
        <div className={styles.fileCard}>
          <div className={styles.fileTitleRow}>
            <h4>{file.typePiece.label}</h4>
            {file.typePiece.source === "DETECTE" && (
              <Chip
                label="Détecté"
                color={ChipColor.Blue}
                className={styles.fileBadge}
              />
            )}
            {file.typePiece.labelDetecte && (
              <Chip
                label={`Détecté : ${file.typePiece.labelDetecte}`}
                color={ChipColor.Orange}
                className={styles.fileBadge}
              />
            )}
            {badge && (
              <Chip
                label={badge.label}
                color={badge.color}
                className={styles.fileBadge}
              />
            )}
          </div>
          <div className={styles.fileCardContent}>
            <small className={styles.fileName}>{file.filename}</small>
            <div className={styles.fileActions}>
              <Button
                variant="text"
                iconVariant="right"
                icon={isThisViewing ? <Loader /> : <PreviewIcon />}
                className={`${styles.download} ${styles.telecharger}`}
                onClick={() =>
                  handleViewFile(
                    file.idFile,
                    file.pathname,
                    file.filename,
                    entreprise.siren,
                    millesime
                  )
                }
                disabled={isBusy}
              >
                <small>Visualiser</small>
              </Button>
              <Button
                variant="text"
                iconVariant="right"
                icon={isThisDownloading ? <Loader /> : <Download />}
                className={`${styles.download} ${styles.telecharger}`}
                onClick={() =>
                  handleDownloadFile(
                    file.idFile,
                    file.pathname,
                    file.filename,
                    entreprise.siren,
                    millesime
                  )
                }
                disabled={isBusy}
              >
                <small>Télécharger</small>
              </Button>
            </div>
          </div>
          <small className={styles.fileDate}>
            (Dépôt le {formatDateHour(new Date(file.createdAt))})
          </small>
        </div>
      </Card>
    );
  };

  return (
    <Collapse
      isOpen={isOpen}
      title={`${totalInitial > 1 ? "Pièces justificatives" : "Pièce justificative"} (${totalInitial})`}
      onToggle={() => setIsOpen((prevState) => !prevState)}
      hasToggleText={false}
    >
      <div className={classNames(styles.filesContainer, styles.scroll)}>
        {axeIsLoading && <SkeletonContent />}
        {!axeIsLoading &&
          displayedFiles.length > 0 &&
          entreprise &&
          millesime && (
            <>
              {totalFiles > 1 && (
                <CardWithBadge
                  className={classNames(styles.card, styles.badgeBorder)}
                  classNameChildren={classNames(styles.badgeContent)}
                  hasBadge
                  badgeVariant="primary"
                  label="Groupe des pièces jointes"
                  fullWidthBadge
                  hideIcon
                >
                  <div className={styles.fileCardContent}>
                    <h4>Fichier .zip</h4>
                    <Button
                      variant="text"
                      iconVariant="right"
                      icon={isZippingFiles ? <Loader /> : <Download />}
                      className={`${styles.download} ${styles.telecharger}`}
                      onClick={() => {
                        triggerExport().catch(() => {});
                      }}
                      disabled={isBusy}
                    >
                      <small>Télécharger</small>
                    </Button>
                  </div>
                  {zipMetadata?.generatedAt && (
                    <small className={styles.zipDate}>
                      Version du{" "}
                      {formatDateHour(new Date(zipMetadata.generatedAt))}{" "}
                      {zipMetadata.isUpToDate ? (
                        <span className={styles.zipUpToDate}>(à jour)</span>
                      ) : (
                        <span className={styles.zipStale}>(non à jour)</span>
                      )}
                    </small>
                  )}
                </CardWithBadge>
              )}
              <small>
                ({displayedFiles.length}{" "}
                {displayedFiles.length > 1 ? "affichées" : "affichée"} sur{" "}
                {totalInitial})
              </small>
              {fileGroups.map((group, index) => {
                const isLatestGroup = index === 0;
                const isGroupOpen =
                  groupOpenOverrides[group.key] ?? isLatestGroup;
                return (
                  <div className={styles.mainSection} key={group.key}>
                    <CollapseCompact
                      isOpen={isGroupOpen}
                      title={group.title}
                      onToggle={() => toggleGroup(group.key, isGroupOpen)}
                    >
                      <div className={styles.sectionFiles}>
                        {group.files.map(renderFileCard)}
                      </div>
                    </CollapseCompact>
                  </div>
                );
              })}
              {totalFiles > FILES_LIMIT && (
                <InfoCard
                  type="info"
                  className={classNames(styles.card, styles.noBorder)}
                >
                  Pour avoir accès à toutes les pièces jointes, téléchargez le{" "}
                  <b>fichier .zip</b>
                </InfoCard>
              )}
            </>
          )}
        {!axeIsLoading && displayedFiles.length === 0 && (
          <div className={styles.card}>
            <small>Aucun fichier ne correspond à votre recherche</small>
          </div>
        )}
      </div>
      <FilterSearch
        variant="full-width"
        search={
          <InputSearch
            variant="full-width"
            name="search"
            placeholder="Rechercher un fichier"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        }
      />
      {!isReadOnly && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={classNames(styles.attachmentZone, {
            [styles.isDragOver]: isDragOver,
            [styles.isAssujetti]: isAssujetti,
          })}
        >
          {optionsTypePiece.length > 0 && (
            <div className={styles.typePieceChoice}>
              <UIInputSelect
                name="typePieceDeclare"
                placeholder="Type de pièce (facultatif)"
                options={optionsTypePiece}
                value={typePieceDeclare}
                isSearchable
                onChange={(value: unknown) =>
                  setTypePieceDeclare(typeof value === "string" ? value : "")
                }
              />
              <p className={styles.typePieceHint}>
                Sans choix, le type est détecté automatiquement.
              </p>
            </div>
          )}
          <FileInputButton
            multiple
            variant="text"
            icon={isAddingFile ? <Loader /> : <AddIcon />}
            className={styles.download}
            onChange={handleAddFile}
            disabled={isAddingFile}
          >
            Ajouter une pièce justificative
          </FileInputButton>
        </div>
      )}
    </Collapse>
  );
}
